import React, { useEffect, useState } from 'react';
import { ActivityIndicator, FlatList, Touchable, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { poll_getPollById } from '../../../store/actionsv1/polls';
import { PollOptionProps, PollProps, PollSummaryProps } from '../../../store/types/auth_svc';
import { BarChart, Bar, Text as RechartText, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Pie, PieChart, Label, LabelList } from 'recharts';
import Colors from '../../../constants/Colorsv2';
import { Text, TouchableOpacity } from '../../../globalComponents/NativeOverrides';
import Switch from '../../Social/components/Switch';
import { Icon } from 'react-native-elements';
import { poll_randomRGBA } from '../helpers/poll';

type PollReviewCardProps = {
    poll_id:string,
    width:number,
    hide_chart?:boolean,
    pinned?:boolean,
    onPin:(poll:PollProps) => void
}

const color_options = [
    Colors.highlights.highlight100Faded,
    Colors.highlights.highlight200Faded,
    Colors.highlights.highlight300Faded,
    Colors.highlights.highlight400Faded,
    Colors.highlights.highlight500Faded,
    poll_randomRGBA(0.25),
    poll_randomRGBA(0.25),
    poll_randomRGBA(0.25)
]

const MIN_WIDTH = 175
const MAX_WIDTH = 550

const PollReviewCard = ({ poll_id, width, onPin, hide_chart, pinned } : PollReviewCardProps) => {
    const [ loading, setLoading ] = useState(false);
    const [ progress_width, setProgressWidth ] = useState(0);
    const [ actions_visible, setActionsVisible ] = useState(false);
    const [ show_chart_option, setShowChartOptions ] = useState(false);
    const [ chart_type, setChartType ] = useState<'Bar'|'Pie'|'Progress'>('Progress');

    const chart_options = ['Bar','Pie','Progress'];

    const dispatch = useDispatch();
    const polls_list: {updated:number, polls:PollProps[]} = useSelector((state:any) => state.poll.polls_list, (left, right) => left.updated == right.updated);
    const poll = polls_list.polls.find(p => p.poll_id == poll_id)

    const poll_options_list: {updated:number, poll_options:PollOptionProps[]} = useSelector((state:any) => state.poll.poll_options_list, (left, right) => left.updated == right.updated);
    const poll_options = poll_options_list.poll_options.filter(po => po.poll_id == poll_id && po.status == 'active')

    const poll_summaries_list: { updated:number, poll_summaries:PollSummaryProps[] } = useSelector((state:any) => state.poll.poll_summaries_list, (left, right) => left.updated == right.updated)
    const poll_summaries = poll_summaries_list.poll_summaries.filter(ps => ps.poll_id == poll_id)

    const total_count = poll_summaries.reduce((a,b) => a + b.count, 0)

    let chart_width = hide_chart ? width : width < MIN_WIDTH ? MIN_WIDTH : width
    chart_width = chart_width > MAX_WIDTH ? MAX_WIDTH : chart_width
    useEffect(() => {
        if(!poll_id){ return }
        getPollDetailsFromServer(poll_id)
    },[poll_id])

    const getPollDetailsFromServer = async(poll_id:string) => {
        setLoading(true);
        await dispatch(poll_getPollById(poll_id))
        setLoading(false);
    }

    const handleSelectChartOption = (chart:'Bar'|'Pie'|'Progress') => {
        setChartType(chart);
        setShowChartOptions(false);
    }

    const renderChartOptions = (data: {item:string, index:number}) => {
        return (
            <TouchableOpacity style={{ padding:10, borderBottomWidth:1, borderColor:Colors.shades.shade100 }} onPress={() => handleSelectChartOption(data.item)}>
                <Text size={12} color={Colors.brand.electric} weight='semibold' textAlign='right'>{data.item}</Text>
            </TouchableOpacity>
        )
    }

    const renderData = (data : { item:any, index:number }) => {
        let progress = progress_width * data.item.pct
        return (
            <View style={{ flexDirection:'row', height:40, marginBottom:5, alignItems:'center', paddingLeft:10, paddingRight:10}}>
                <Text style={{ flex:1 }} size={12} color={Colors.brand.midnight} weight='semibold'>{data.item.name}</Text>
                <Text size={12} color={Colors.brand.midnight} weight='semibold' textAlign='right'>{data.item.count} ({(data.item.pct*100).toFixed()}%)</Text>
                <View nativeID={data.item.value} style={{ position:'absolute', left:0, width: progress, height:40, backgroundColor:Colors.highlights.highlight500Faded, borderTopRightRadius:8, borderBottomRightRadius: 8 }} />
            </View>
        )
    }

    let data = poll_options.sort((a,b) => a.poll_option_id - b.poll_option_id).map((o,i) => {
        let summary = poll_summaries.find(s => s.poll_option_id == o.poll_option_id)
        if(!summary){ summary = { poll_id, poll_option_id:o.poll_option_id, count: 0, pct:0 } }
        let fill = color_options[i]
        if(!fill){ fill = poll_randomRGBA(0.25) }

        return {
            value: o.option_value,
            name: o.option_name,
            count: summary.count,
            pct: summary.pct,
            fill: `${fill}`
        }
    })

    if(!poll){ return (<></>) }

    if(hide_chart){
        return (
            <TouchableOpacity style={{ flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.shades.shade600 }} onPress={() => onPin(poll)}>
                <View style={{ backgroundColor:Colors.shades.shade100, padding:10, flex:1 }}>
                    <Text size={14} color={Colors.brand.midnight} weight='semibold'>{poll.poll_question}</Text>
                    <Text style={{ marginTop:5 }} size={12} color={Colors.brand.midnight} weight='regular'>{total_count} responses</Text>
                </View>
                <Icon name='bar-chart' type='feather' size={20} color={Colors.brand.midnight} />
            </TouchableOpacity>
            
        )
    }

    return (
        <View style={{ flex:1, justifyContent:'space-between', width:chart_width, height:chart_width, backgroundColor:Colors.shades.white, borderRadius:8, ...local_styles.raised }}>
            <View style={{ padding:10, margin:5, borderRadius:8, backgroundColor:Colors.shades.shade100 }}>
                <Text size={14} color={Colors.brand.midnight} weight='bold' textAlign='center'>{poll.poll_question}</Text>
                {poll.status == 'closed' ?
                <Text style={{ marginTop:5 }} size={12} color={Colors.brand.slate} weight='bold' textAlign='center'>Poll Closed</Text>
                :<></>}
            </View>
            {poll && total_count > 0 && !hide_chart && poll.show_responses ?
            <View nativeID='poll_chart' style={{ padding:10 }}>
                {chart_type == 'Bar' ?
                <ResponsiveContainer width={chart_width - 20} height={chart_width - 20}>
                    <BarChart
                        barCategoryGap={1}
                        width={chart_width - 20}
                        height={chart_width - 20}
                        data={data}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                    <XAxis 
                        dataKey="value" 
                        interval={0}
                        textAnchor='start'
                        axisLine={false}
                        tickLine={false}
                        fontFamily='barlow-semibold'
                        fontSize={14}
                        tick={{fill: Colors.brand.midnight}}
                    />
                    <Tooltip 
                        wrapperStyle={{ outline: 'none' }}
                        cursor={false}
                        content={<CustomTooltip/>}
                    />
                    <Bar 
                        isAnimationActive={false}
                        dataKey='count' 
                        radius={[10, 10, 0, 0]}
                    >
                        {pinned ?
                        <LabelList
                            rotate={-90}
                            dataKey='name' 
                            content={<CustomLabelList />}
                            />
                            :<></>}
                        </Bar>
                        <Legend

                            verticalAlign='bottom'
                            content={<CustomLegend data={data} width={chart_width - 20}/>}
                        />                    
                    </BarChart>
                </ResponsiveContainer>
                : chart_type == 'Pie' ?
                <ResponsiveContainer width={chart_width - 20} height={chart_width - 20}>
                    <PieChart 
                    width={chart_width - 20}
                    height={chart_width - 20}
                    >
                    <Legend
                        verticalAlign='bottom'
                        content={<CustomLegend data={data} width={chart_width - 20}/>}
                        />
                    <Pie
                        dataKey="count"
                        isAnimationActive={false}
                        data={data}
                        label={false}
                        cx="50%"
                        cy="50%"
                        outerRadius={chart_width / 2.5}
                        innerRadius={chart_width / 5}
                        fill={Colors.brand.midnight}
                    >
                        <LabelList 
                            dataKey='count' 
                            fontSize={14}
                            fontFamily='barlow-bold'
                            fill={Colors.brand.midnight}
                            />
                        </Pie>
                    <Tooltip 
                        wrapperStyle={{ outline: 'none' }}
                        cursor={false}
                        content={<CustomTooltip/>}
                    />
                    </PieChart>
                </ResponsiveContainer>
                : chart_type == 'Progress' ?
                <View onLayout={(ev) => {
                    const { width } = ev.nativeEvent.layout;
                    setProgressWidth(width)
                }}>
                    <FlatList 
                        data={data.sort((a,b) => b.count - a.count)}
                        renderItem={renderData}
                    />
                </View>
                :<></>}
            </View>
            : poll && !poll.show_responses ?
            <View style={{ padding:10, justifyContent:'center', alignItems:'center' }}>
                <Text size={14} color={Colors.brand.midnight} weight='semibold' textAlign='center'>Responses Currently Hidden By Poll Admin</Text>
            </View>
            : poll && hide_chart ?
            <TouchableOpacity style={{ borderWidth:1, margin:5, borderColor:Colors.brand.electric, borderRadius:4, padding:10 }} onPress={() => onPin(poll)}>
                <Text size={12} color={Colors.brand.electric} weight='regular' textAlign='center'>View Poll</Text>
            </TouchableOpacity>
            : poll && !loading ?
            <View style={{ height: chart_width, padding:10, justifyContent:'center', alignItems:'center' }}>
                <Text size={14} color={Colors.brand.midnight} weight='semibold' textAlign='center'>No Responses Received</Text>
            </View>
            :
            <View style={{ height: chart_width, justifyContent:'center', alignItems:'center' }}>
                <ActivityIndicator size={'large'} color={Colors.brand.midnight}/>
            </View>
            }
            {!hide_chart ?
            <View nativeID='poll_actions' style={{ backgroundColor:Colors.shades.shade100, borderBottomRightRadius:8, borderBottomLeftRadius:8, padding:10 }}>
                <TouchableOpacity style={{ padding:5, alignItems: 'flex-end' }} onPress={() => setActionsVisible(!actions_visible)}>
                    <Icon name={actions_visible ? 'minimize-2' : 'maximize-2'} type='feather' size={12} color={Colors.brand.electric} />
                </TouchableOpacity>
                {actions_visible ?
                <View>
                    <View style={{ padding: 10, flexDirection:'row', alignItems:'center' }}>
                        <Text style={{ flex:1 }} size={12} color={Colors.brand.midnight} weight='semibold'>Pin Poll</Text>
                        <Switch
                            switch_type='on_off'
                            value={pinned?true:false}
                            onChange={() => onPin(poll)}
                        />
                    </View>
                    <TouchableOpacity style={{ padding: 10, flexDirection:'row', alignItems:'center' }} onPress={() => setShowChartOptions(!show_chart_option)}>
                        <Text style={{ flex:1 }} size={12} color={Colors.brand.midnight} weight='semibold'>Chart Type</Text>
                        <Icon name={show_chart_option?'chevron-up':'chevron-right'} type='feather' color={Colors.brand.electric} size={12} />
                        {show_chart_option ?
                        <View style={{ position:'absolute', minWidth:100, bottom:30, padding:10, right:0, borderRadius:8, backgroundColor:Colors.shades.white, ...local_styles.raised }}>
                            <FlatList 
                                data={chart_options}
                                renderItem={renderChartOptions}
                            />
                        </View>
                        :<></>}
                    </TouchableOpacity>
                </View>
                :<></>
                }
            </View>
            :<></>}
        </View>
    )
}


const CustomLabelList = (props) => {
    const { x, y, width, height, value } = props;
    
    let label_value = value as string
    if(label_value.length > 45){
        label_value = `${label_value.substring(0, 45)}...`
    }

    return (
      <g>
        <RechartText 
            angle={-90} 
            x={x + width / 2} 
            y={y + height - 10}
            //scaleToFit
            fontFamily='barlow-semibold'
            fontSize={16}
            fill={Colors.brand.midnight} 
            textAnchor="start" 
            dominantBaseline="middle"
            >
          {value}
        </RechartText>
      </g>
    );
  };

const CustomTooltip = ({ active, payload, label }:any) => {
    if(!active || !payload || !payload.length){ return <></> }
    let data = payload[0]?.payload
    if(!data){ return <></> }
    return (
        <View style={{ padding:5, minWidth: 150, borderRadius:4, backgroundColor:Colors.shades.white, ...local_styles.raised }}>
            <Text size={12} color={Colors.brand.midnight} weight='semibold'>{data.name}</Text>
            <View style={{ flexDirection:'row', marginTop:5 }}>
                <Text style={{ flex:2/3 }} size={12} color={Colors.brand.slate} weight='regular'>Responses</Text>
                <Text style={{ flex:1/3 }} size={12} color={Colors.brand.midnight} weight='bold' textAlign='right'>{data.count}</Text>
            </View>
            <View style={{ flexDirection:'row', marginTop:5 }}>
                <Text style={{ flex:2/3 }} size={12} color={Colors.brand.slate} weight='regular'>Percent</Text>
                <Text style={{ flex:1/3 }} size={12} color={Colors.brand.midnight} weight='bold' textAlign='right'>{(data.pct*100).toFixed(2)}%</Text>
            </View>
        </View>
    );
  };

const MAX_LEGEND_WIDTH = 200
const CustomLegend = ({ data, width }:any) => {
    const [ collapsed, setCollapsed ] = useState(true);
    if(!data || data.length == 0){ return <></> }
    return (
        <View style={{ position: 'absolute', bottom:0, left:0, alignItems:'flex-start' }}>
            <View style={{ width:!collapsed && width ? width > MAX_LEGEND_WIDTH ? MAX_LEGEND_WIDTH : width : undefined, padding:5, borderRadius:4, backgroundColor:Colors.shades.white, ...local_styles.raised }}>
                {!collapsed ?
                <View nativeID='legend_items'>
                    {data.map((p:any) => {
                        return (
                            <View style={{ flexDirection:'row', padding:3, alignItems:'center' }}>
                                <View style={{ flex:1, flexDirection:'row', alignItems:'center' }}>
                                    <View style={{ height:10, width:10, backgroundColor:p.fill, borderRadius:100, marginRight:10 }} />
                                    <Text size={12} color={Colors.brand.midnight} weight='regular'>{p.name} ({p.value})</Text>
                                </View>
                                <Text size={12} weight='bold' color={Colors.brand.midnight}>{p.count} ({(p.pct*100).toFixed()}%)</Text>
                            </View>
                        )
                    })}
                </View>
                :<></>}
                <TouchableOpacity style={{ padding:10, alignSelf:'flex-start' }} onPress={() => setCollapsed(!collapsed)}>
                    {!collapsed ?
                    <Icon name='minimize-2' type='feather' color={Colors.brand.electric} size={12}/>
                    :
                    <Icon name='maximize-2' type='feather' color={Colors.brand.electric} size={12}/>
                    }
                </TouchableOpacity>
            </View>
        </View>
    )
}


const local_styles = {
    raised: { shadowColor: "rgba(0, 0, 0, 0.12)", shadowOffset: { width: 0, height: 10 }, shadowRadius: 10, shadowOpacity: 1 }
}

export default PollReviewCard